import { parse, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import moment from 'moment-timezone';

type CalendarDate = Date | moment.Moment;

const DEFAULT_TIMEZONE = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'America/Los_Angeles';
const DATE_FNS_YYYY_MM_DD: string = 'yyyy-MM-dd'; // e.g. "2025-08-20"
const DATE_FNS_MM_DD_YYYY = 'MM/dd/yyyy'; // e.g. "08/20/2025"
const DATE_FNS_12_HOUR_TIME = 'h:mm a'; // e.g. "11:59 pm"
const DATE_FNS_DATE_WITH_DAY_OF_WEEK = 'EEE - MMM d, yyyy'; // e.g. "Wed - Aug 20, 2025"
const DATE_FNS_TIMEZONE_ABBREVIATION = 'zzz'; // e.g. "PDT"
const DATE_FNS_DATE_TIME_WITH_TIMEZONE = `${DATE_FNS_DATE_WITH_DAY_OF_WEEK} ${DATE_FNS_12_HOUR_TIME} ${DATE_FNS_TIMEZONE_ABBREVIATION}`; // e.g. "Wed - Aug 20, 2025 11:59 pm PDT"

const modifyDateInTimeZone = function (
  date: Date,
  tz: string,
  fn: (d: Date, opts?: { [key: string]: unknown }) => Date,
  options?: { [key: string]: unknown },
): Date {
  const zonedDate: Date = toZonedTime(date, tz);
  const modifiedDate: Date = options ? fn(zonedDate, options) : fn(zonedDate);

  return fromZonedTime(modifiedDate, tz);
};

const parseYyyyMmDdInTimeZone = function (
  yyyyMmDdStr: string, // e.g. 2018-10-04 => October 4, 2018
  date: Date,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): Date {
  const zonedDate: Date = toZonedTime(date, timezone);
  const parsedDate: Date = parse(yyyyMmDdStr, DATE_FNS_YYYY_MM_DD, zonedDate);

  return fromZonedTime(parsedDate, timezone);
};

const isMoment = function (obj: unknown): boolean {
  return obj?.constructor.name === 'Moment';
};

// Convert 11:59pm in one timezone to 11:59pm in another timezone
const sameTimeInTimeZone = function (date: Date, toTz: string, fromTz: string): Date {
  return toZonedTime(fromZonedTime(new Date(date.toJSON()), toTz), fromTz);
};

const ensurePlainJsDate = function (date: CalendarDate | string, timezone: string): Date {
  if (typeof date === 'string') return parseYyyyMmDdInTimeZone(date, new Date(), timezone);

  if (isMoment(date)) {
    const tz: string = `${moment().tz() || DEFAULT_TIMEZONE}`;

    return sameTimeInTimeZone(new Date((<moment.Moment>date).toDate()), timezone, tz);
  }

  return new Date(<Date>date);
};

const isInDateRange = function (date: Date, startDate: Date, endDate: Date): boolean {
  return isAfter(date, startDate) && isBefore(date, endDate);
};

const formatDateTimeInSystemTimezone = function (date: Date): string {
  return `${formatInTimeZone(date, DEFAULT_TIMEZONE, DATE_FNS_DATE_TIME_WITH_TIMEZONE)} (your time)`;
};

const dayBoundaryFromQP = function (qp: string | null | undefined, timezone: string, boundary: 'start' | 'end'): Date {
  const raw = (qp ?? '').trim();
  let base: Date = new Date();

  if (raw) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      base = parseYyyyMmDdInTimeZone(raw, new Date(), timezone);
    } else {
      const candidate = new Date(raw);
      base = Number.isNaN(candidate.getTime()) ? new Date() : candidate;
    }
  }

  return modifyDateInTimeZone(base, timezone, boundary === 'start' ? startOfDay : endOfDay);
};

const startOfDayFromQP = function (qp: string | null | undefined, timezone: string): Date {
  return dayBoundaryFromQP(qp, timezone, 'start');
};

const endOfDayFromQP = function (qp: string | null | undefined, timezone: string): Date {
  return dayBoundaryFromQP(qp, timezone, 'end');
};

export {
  CalendarDate,
  DATE_FNS_MM_DD_YYYY,
  DATE_FNS_YYYY_MM_DD,
  DATE_FNS_12_HOUR_TIME,
  DATE_FNS_DATE_TIME_WITH_TIMEZONE,
  DATE_FNS_DATE_WITH_DAY_OF_WEEK,
  DEFAULT_TIMEZONE,
  endOfDayFromQP,
  startOfDayFromQP,
  ensurePlainJsDate,
  formatDateTimeInSystemTimezone,
  isInDateRange,
  isMoment,
  modifyDateInTimeZone,
  parseYyyyMmDdInTimeZone,
  sameTimeInTimeZone,
};
